import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { GameSession } from '@/components/game/game-session'
import { supabase } from '@/lib/supabase'
import { canAffordGame, startGame } from '@/lib/games'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Trophy, Crown, Star } from "lucide-react"

export default function GameSessionPage() {
  const [currentAttempt, setCurrentAttempt] = useState(1)
  const [scores, setScores] = useState<number[]>([])
  const { gameId } = useParams()
  const navigate = useNavigate()
  const [gameCost, setGameCost] = useState<number>(0)
  const { user } = useAuth()

  useEffect(() => {
    if (gameId) {
      loadGameCost()
    }
  }, [gameId])

  async function loadGameCost() {
    const { data, error } = await supabase
      .from('games')
      .select('cost_per_game')
      .eq('id', gameId)
      .single()

    if (!error && data) {
      setGameCost(data.cost_per_game)
    }
  }

  const handleGameComplete = async (score: number, responseTime: number) => {
    if (!user || !gameId) return

    const { data: existingScores, error: checkError } = await supabase
      .from('game_scores')
      .select('id, score')
      .eq('game_id', gameId)
      .eq('user_id', user.id)
      .eq('attempt_number', currentAttempt)

    if (checkError) {
      console.error('Error checking existing score:', checkError)
      alert('There was an error saving your score. Please try again.')
      return
    }

    // If score exists and is higher than current score, skip saving
    if (existingScores?.length > 0) {
      const existingScore = existingScores[0]
      if (existingScore.score >= score) {
        setScores([...scores, existingScore.score])
        setCurrentAttempt(currentAttempt + 1)
        return
      }

      // Delete existing lower score
      const { error: deleteError } = await supabase
        .from('game_scores')
        .delete()
        .eq('id', existingScore.id)

      if (deleteError) {
        console.error('Error deleting existing score:', deleteError)
        alert('There was an error saving your score. Please try again.')
        return
      }
    }

    try {
      const { error: insertError } = await supabase
        .from('game_scores')
        .insert({
          game_id: gameId,
          user_id: user.id,
          attempt_number: currentAttempt,
          score,
          response_time: responseTime
        })

      if (insertError) throw insertError

      setScores([...scores, score])
      setCurrentAttempt(currentAttempt + 1)
    } catch (err) {
      console.error('Failed to save score:', err)
      alert('There was an error saving your score. Please try again.')
    }
  }

  const handlePlayAgain = async () => {
    if (!user || !gameId) return

    if (currentAttempt > 3) {
      alert('Maximum attempts reached')
      navigate('/games')
      return
    }

    try {
      const canAfford = await canAffordGame(user.id, gameId)
      if (!canAfford) {
        alert('Insufficient Voy Coins for another attempt')
        return
      }

      const started = await startGame(user.id, gameId)
      if (started) {
        setScores([])
      }
    } catch (error) {
      console.error('Failed to start new attempt:', error)
      alert('Failed to start new attempt. Please try again.')
    }
  }

  const handleFinish = () => {
    navigate('/games')
  }

  if (!gameId || !user) {
    return (
      <div className="container mx-auto py-6">
        <p className="text-center text-muted-foreground">
          Invalid game session
        </p>
      </div>
    )
  }

  if (scores.length > 0) {
    return (
      <div className="container mx-auto py-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center pb-2">
            <CardTitle className="flex items-center justify-center gap-3 text-3xl">
              <Trophy className="h-8 w-8 text-yellow-400" />
              <span className="bg-gradient-to-r from-primary to-primary/60 text-transparent bg-clip-text">
                Game Complete!
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 pt-6">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                            bg-gradient-to-r from-primary/10 to-transparent">
                <Star className="h-5 w-5 text-yellow-400" />
                <p className="text-2xl font-bold">
                  {scores[scores.length - 1].toLocaleString()}
                </p>
              </div>
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Crown className="h-4 w-4" />
                Attempt {currentAttempt - 1} of 3
              </p>
            </div>

            {currentAttempt <= 3 ? (
              <Button 
                className="w-full transition-all hover:scale-105 hover:shadow-lg
                          bg-gradient-to-r from-primary to-primary/90" 
                onClick={handlePlayAgain}
              >
                <div className="flex items-center gap-2">
                  <span>Play Again</span>
                  <span className="bg-primary-foreground/10 px-2 py-0.5 rounded-full text-sm">
                    {gameCost} Coins
                  </span>
                </div>
              </Button>
            ) : (
              <Button 
                className="w-full transition-all hover:scale-105" 
                onClick={handleFinish}
              >
                View All Games
              </Button>
            )}
            <div className="text-center text-sm text-muted-foreground animate-pulse">
              Keep playing to improve your score!
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 min-h-[calc(100vh-3.5rem)] flex flex-col">
      <div className="max-w-2xl mx-auto">
        <GameSession 
          gameId={gameId} 
          onComplete={handleGameComplete}
        />
      </div>
    </div>
  )
}